import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import {
  PageContainer,
  PaymentCard,
  PaymentBrandPanel,
  CheckoutPanel,
  Header,
  Title,
  Subtitle,
  SummaryBox,
  AmountRow,
  AmountLabel,
  Amount,
  DetailRow,
  DetailLabel,
  DetailValue,
  AlertBox,
  PrimaryButton,
  SecondaryButton,
  Footer,
  PoweredBy,
  SecureMark,
} from './PaymentVisuals';

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true), { once: true });
      existing.addEventListener('error', () => resolve(false), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const RazorpayPayment = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const autoStartAttempted = useRef(false);

  // Required params
  const amount = searchParams.get('amount');
  const paymentType = searchParams.get('paymentType') || 'general';
  const parentOrigin = decodeURIComponent(searchParams.get('origin') || '');
  const mode = searchParams.get('mode');
  const source = searchParams.get('source') || 'willovate';

  // Optional metadata
  const planId = searchParams.get('planId');
  const planName = searchParams.get('planName');
  const restaurantId = searchParams.get('restaurantId');
  const restaurantName = searchParams.get('restaurantName');
  const orderId = searchParams.get('orderId');
  const explicitReferenceId = searchParams.get('referenceId');
  const explicitPaymentPurpose = searchParams.get('paymentPurpose');
  const customerName = searchParams.get('customerName');
  const customerPhone = searchParams.get('customerPhone');
  const customerEmail = searchParams.get('customerEmail');

  const sendResultToParent = useCallback((result) => {
    if (window.opener && parentOrigin) {
      window.opener.postMessage(result, parentOrigin);
    } else {
      console.warn('Cannot send result — no opener or origin');
    }
  }, [parentOrigin]);

  const handleCancel = useCallback(() => {
    sendResultToParent({
      type: 'PAYMENT_RESULT',
      status: 'CANCELLED',
      message: 'Payment cancelled by user',
    });
    window.close();
  }, [sendResultToParent]);

  const initiatePayment = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.willovate.in';

      const scriptOk = await loadRazorpayScript();
      if (!scriptOk || !window.Razorpay) {
        setError('Could not load Razorpay checkout script.');
        setLoading(false);
        return;
      }

      const orderRes = await fetch(`${apiUrl}/api/payment/razorpay/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Number(amount),
          currency: 'INR',
          source,
          paymentType,
          paymentPurpose: explicitPaymentPurpose || undefined,
          planId: planId ? Number(planId) : undefined,
          planName: planName || undefined,
          restaurantId: restaurantId ? Number(restaurantId) : undefined,
          referenceId: explicitReferenceId || undefined,
          orderId: orderId || undefined,
          customerName: customerName || undefined,
          customerPhone: customerPhone || undefined,
          customerEmail: customerEmail || undefined,
          receipt: orderId
            ? `${source}-${orderId}`
            : `${source}-${Date.now()}`,
        }),
      });

      if (!orderRes.ok) {
        const failure = await orderRes.json().catch(() => null);
        throw new Error(failure?.message || `Razorpay order request failed (${orderRes.status}).`);
      }

      const orderJson = await orderRes.json();
      const order = orderJson?.data;

      if (!orderJson?.success || !order?.orderId || !order?.keyId) {
        throw new Error(orderJson?.message || 'Order creation returned no orderId / keyId');
      }

      // Notify parent that the order has been created — same intent as PhonePe's PAYMENT_INITIATED.
      sendResultToParent({
        type: 'PAYMENT_INITIATED',
        provider: 'razorpay',
        razorpayOrderId: order.orderId,
        merchantTransactionId: order.orderId,
        amount: Number(amount),
        paymentType,
      });

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amountInPaise,
        currency: order.currency || 'INR',
        name: 'Willovate',
        description: planName || restaurantName || paymentType,
        order_id: order.orderId,
        prefill: {
          name: customerName || '',
          email: customerEmail || '',
          contact: customerPhone || '',
        },
        theme: { color: '#0F1C3D' },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${apiUrl}/api/payment/razorpay/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              }),
            });
            const verifyJson = await verifyRes.json();
            const verified = !!verifyJson?.success;
            const verification = verifyJson?.data;

            sendResultToParent({
              type: 'PAYMENT_RESULT',
              provider: 'razorpay',
              status: verified ? 'SUCCESS' : 'FAILED',
              transactionId: response.razorpay_payment_id,
              merchantTransactionId: order.orderId,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              amount: Number(amount),
              message: verified ? (verification?.message || 'Payment successful') : (verifyJson?.message || 'Signature verification failed'),
            });

            if (verified) {
              setSuccess(true);
              setTimeout(() => window.close(), 1500);
            } else {
              setError(verifyJson?.message || 'Signature verification failed');
              setLoading(false);
            }
          } catch (verifyErr) {
            setError(`Verification failed: ${verifyErr.message}`);
            sendResultToParent({
              type: 'PAYMENT_RESULT',
              provider: 'razorpay',
              status: 'FAILED',
              message: verifyErr.message,
            });
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            sendResultToParent({
              type: 'PAYMENT_RESULT',
              provider: 'razorpay',
              status: 'CANCELLED',
              message: 'Checkout closed without payment',
            });
          },
        },
      });

      rzp.open();
    } catch (err) {
      console.error('Razorpay payment error:', err);
      const message = err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')
        ? 'Backend API not reachable. Check REACT_APP_API_URL and that the Razorpay endpoints are deployed.'
        : `Payment initiation failed: ${err.message}`;
      setError(message);
      setLoading(false);
    }
  }, [amount, customerEmail, customerName, customerPhone, explicitPaymentPurpose, explicitReferenceId, orderId, paymentType, planId, planName, restaurantId, restaurantName, sendResultToParent, source]);

  useEffect(() => {
    if (!amount || !parentOrigin || mode !== 'popup') {
      setError('Invalid payment parameters');
    }
  }, [amount, parentOrigin, mode]);

  useEffect(() => {
    if (autoStartAttempted.current || !amount || !parentOrigin || mode !== 'popup') return;
    autoStartAttempted.current = true;
    void initiatePayment();
  }, [amount, initiatePayment, mode, parentOrigin]);

  return (
    <PageContainer>
      <PaymentCard>
        <PaymentBrandPanel gateway="Razorpay" />
        <CheckoutPanel>
          <Header>
            <span>Checkout / Razorpay</span>
            <Title>Opening secure payment</Title>
            <Subtitle>Razorpay opens automatically. Use retry only if the checkout could not start.</Subtitle>
          </Header>

        <SummaryBox>
          <AmountRow>
            <AmountLabel>Amount to Pay</AmountLabel>
            <Amount>₹{amount}</Amount>
          </AmountRow>

          {planName && (
            <DetailRow>
              <DetailLabel>Plan</DetailLabel>
              <DetailValue>{planName}</DetailValue>
            </DetailRow>
          )}

          {restaurantName && (
            <DetailRow>
              <DetailLabel>Business</DetailLabel>
              <DetailValue>{restaurantName}</DetailValue>
            </DetailRow>
          )}

          {orderId && (
            <DetailRow>
              <DetailLabel>Order ID</DetailLabel>
              <DetailValue>{orderId}</DetailValue>
            </DetailRow>
          )}

          {customerName && (
            <DetailRow>
              <DetailLabel>Customer</DetailLabel>
              <DetailValue>{customerName}</DetailValue>
            </DetailRow>
          )}

          <DetailRow>
            <DetailLabel>Source</DetailLabel>
            <DetailValue style={{ textTransform: 'capitalize' }}>{source}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailLabel>Payment Type</DetailLabel>
            <DetailValue style={{ textTransform: 'capitalize' }}>{paymentType}</DetailValue>
          </DetailRow>
        </SummaryBox>

        {error && (
          <AlertBox>
            <XCircle size={20} />
            <span>{error}</span>
          </AlertBox>
        )}

        {success && (
          <AlertBox $success>
            <CheckCircle size={20} />
            <span>Payment successful! Closing window...</span>
          </AlertBox>
        )}

        {!success && (
          <>
            {(loading || error) && (
              <PrimaryButton onClick={initiatePayment} disabled={loading || !amount || !parentOrigin || mode !== 'popup'}>
                {loading ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    <span>Opening Razorpay...</span>
                  </>
                ) : (
                  <span>Try Razorpay again</span>
                )}
              </PrimaryButton>
            )}

            <SecondaryButton onClick={handleCancel} disabled={loading}>
              Cancel
            </SecondaryButton>
          </>
        )}

        <Footer>
          <SecureMark>Your payment is secure and encrypted</SecureMark>
          <PoweredBy>
            <span>Powered by</span>
            <strong>Razorpay</strong>
          </PoweredBy>
        </Footer>
        </CheckoutPanel>
      </PaymentCard>
    </PageContainer>
  );
};

export default RazorpayPayment;
